// src/lib/posthog/remote-config.ts
//
// `init-options.ts` sets `advanced_disable_flags: true` so posthog-js
// never sends its `/flags/` request — the request that leaked the raw page
// URL. That option also switches off the SDK's normal remote-config load,
// and posthog-js 1.424.0 only registers `$session_recording_remote_config`
// (and starts session replay) once a remote config has been applied. With
// `/flags/` off and no config ever applied, replay never starts.
//
// posthog-js's config loader reads a PRELOADED config first: `load()` takes
// `window._POSTHOG_REMOTE_CONFIG?.[token]?.config` before it ever checks
// whether flags are disabled. Preloading it here keeps `/flags/` off while
// still applying the config that starts replay.
//
// `POSTHOG_REMOTE_CONFIG` below is a verbatim copy of this project's
// `/array/<token>/config` response body, measured on 2026-09-24 in a
// headed-browser check, except for `surveys`, which is turned on on purpose
// (see the comment on that line). A change in the PostHog UI to replay,
// heatmap or performance settings needs an edit here to match.
// `_POSTHOG_REMOTE_CONFIG` is a posthog-js internal, not a public API, so a
// posthog-js upgrade must re-run that browser check (no `/flags/` request,
// replay still starts).
// Local init and per-surface options still win over this literal
// (heatmaps, autocapture, dead clicks, replay, console recording), so it
// cannot re-enable what `replay-policy.ts` turns off.

declare global {
  interface Window {
    _POSTHOG_REMOTE_CONFIG?: Record<string, { config: unknown; siteApps: unknown[] }>;
  }
}

export const POSTHOG_REMOTE_CONFIG = {
  analytics: { endpoint: "/i/v0/e/" },
  autocaptureExceptions: false,
  autocapture_opt_out: false,
  captureDeadClicks: false,
  capturePerformance: {
    network_timing: true,
    web_vitals: true,
    web_vitals_allowed_metrics: null,
  },
  conversations: false,
  defaultIdentifiedOnly: true,
  elementsChainAsString: true,
  errorTracking: {
    autocaptureExceptions: false,
    suppressionRules: [],
  },
  hasFeatureFlags: false,
  heatmaps: {
    captureMode: "url_allowlist",
    urlAllowlist: [],
    urlAllowlistEnforced: false,
  },
  logs: { captureConsoleLogs: false },
  productTours: false,
  push: { appIds: [] },
  sdkVersion: { requested: "1" },
  sessionRecording: {
    canvasFps: null,
    canvasQuality: null,
    // Measured true, but the app's own `enable_recording_console_log: false`
    // (init-options.ts) wins over this remote value.
    consoleLogRecordingEnabled: true,
    endpoint: "/s/",
    eventTriggers: [],
    linkedFlag: null,
    masking: null,
    minimumDurationMilliseconds: null,
    networkPayloadCapture: null,
    recordCanvas: false,
    recorderVersion: "v2",
    sampleRate: null,
    scriptConfig: { script: "posthog-recorder" },
    triggerMatchType: null,
    urlBlocklist: [],
    urlTriggers: [],
    version: 1,
  },
  siteApps: [],
  supportedCompression: ["gzip", "gzip-js"],
  // Deliberately differs from the measured 2026-09-24 response: surveys are
  // turned on here so posthog-js loads them without `/flags/`. Targeting must
  // be URL + wait period with schedule "Always", because flag-based targeting
  // (person properties, the survey's internal targeting flag) cannot
  // evaluate with flags off.
  surveys: true,
};

/**
 * Preloads the project's remote config under `key` before `posthog.init`
 * runs. posthog-js's loader reads `window._POSTHOG_REMOTE_CONFIG`
 * first, ahead of the flags-disabled check, so this keeps `/flags/` off
 * while still applying the config that starts session replay. Other keys of
 * the object are preserved: only `key` is written.
 */
export function preloadRemoteConfig(key: string): void {
  if (typeof window === "undefined") return;
  window._POSTHOG_REMOTE_CONFIG = {
    ...window._POSTHOG_REMOTE_CONFIG,
    [key]: { config: POSTHOG_REMOTE_CONFIG, siteApps: [] },
  };
}
